declare const GPUMapMode: { readonly WRITE: number };

interface StagingBuffer {
    buffer: { mapAsync(mode: number): Promise<void> } | null;
    onAvailable(): void;
    destroy(device: StagingRecoveryDevice): void;
}

interface StagingRecoveryDevice {
    dynamicBuffers?: {
        pendingStagingBuffers: StagingBuffer[];
        stagingBuffers: StagingBuffer[] | null;
        onCommandBuffersSubmitted(): void;
    };
    contextLost?: boolean;
    _destroyed?: boolean;
    loseContext(): void;
    fire(event: string): void;
}

/** PlayCanvas 2.21's staging-buffer reuse leaves mapAsync rejections unhandled.
 * 2.23 handles them itself (mapBufferAsync destroys a buffer whose mapping failed);
 * this replacement is kept because it also signals device loss when a mapping fails
 * while the device is active, which the engine does not.
 * Patch only this device's pool; never change browser prototypes or suppress
 * global errors. Canceled mappings must not return a buffer to the active pool. */
export function installWebGPUStagingRecovery(device: StagingRecoveryDevice): void {
    const pool = device.dynamicBuffers;
    if (!pool?.pendingStagingBuffers) return;
    pool.onCommandBuffersSubmitted = () => {
        const pending = pool.pendingStagingBuffers.splice(0);
        for (const staging of pending) {
            const release = () => {
                if (!staging.buffer) return;
                try { staging.destroy(device); }
                catch (error) { console.warn('[StorySplat] Staging buffer cleanup failed:', error); }
            };
            const fail = (error: Error) => {
                release();
                if (!device.contextLost && !device._destroyed) {
                    console.warn('[StorySplat] GPU staging buffer mapping failed:', error);
                    device.loseContext();
                    device.fire('devicelost');
                }
            };
            try {
                void staging.buffer?.mapAsync(GPUMapMode.WRITE).then(() => {
                    if (!pool.stagingBuffers || device.contextLost || device._destroyed) {
                        release();
                        return;
                    }
                    staging.onAvailable();
                    pool.stagingBuffers.push(staging);
                }).catch(fail);
            } catch (error) {
                fail(error instanceof Error ? error : new Error(String(error)));
            }
        }
    };
}
